import type { CoverableOnsaDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import type { AdultAccidentOnsaDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/accident';
import type { CoverageDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto';
import type { CoverageTextItem } from '../../../recoil/types/hippoTypes';

type InputComponent = React.ComponentType<{
    coverageId: string;
    setSelectedDeductible: React.Dispatch<React.SetStateAction<string>>;
    isLoading: boolean;
}>;

interface InputModalProps {
    modalHeader?: string;
    modalSubheader?: string;
}

interface ConfirmationModalProps {
    modalHeader?: string;
    modalSubheader?: string;
}

interface AdditionalCoverageProps extends CoverageDTO {
    input?: InputComponent;
    coverageDescription?: string;
    shouldShowCallMeModal?: boolean;
    shouldShowConfirmationModal?: boolean;
    inputModalProps?: InputModalProps;
    confirmationModalProps?: ConfirmationModalProps;
    shouldShowCovInfoButton?: boolean;
    includedCoverageText?: string[];
    excludedCoverageText?: string[];
}

const getCoveragesProps = (flavourName: string): AdditionalCoverageProps[] => {
    switch (flavourName) {
        case 'Ulykkesforsikring':
            return [
                { coverageId: 'ACCHighRiskSportCov', shouldShowCallMeModal: true },
                { coverageId: 'ACCDeathCov', shouldShowCallMeModal: true }
            ];
        case 'Ulykkesforsikring til unge':
            return [{ coverageId: 'ACCHighRiskSportCov', shouldShowCallMeModal: true }];
        case 'Indboforsikring':
            return [
                { coverageId: 'PRCBikeComprehensiveCov' },
                {
                    coverageId: 'PRCGlassSanitationCov',
                    shouldShowConfirmationModal: true,
                    confirmationModalProps: {
                        modalHeader: 'Du har muligvis allerede denne dækning'
                    }
                }
            ];
        case 'Indboforsikring til unge':
            return [
                {
                    coverageId: 'PRCGlassSanitationCov',
                    shouldShowConfirmationModal: true,
                    confirmationModalProps: {
                        modalHeader: 'Du har muligvis allerede denne dækning'
                    }
                }
            ];
        case 'Husforsikring':
            return [
                {
                    coverageId: 'PRHCosmeticCov',
                    shouldShowCovInfoButton: true
                },
                {
                    coverageId: 'PRHHouseownerAssistanceCov',
                    shouldShowCovInfoButton: true
                }
            ];
        case 'Fritidshusforsikring':
            return [
                {
                    coverageId: 'PRHCosmeticCov',
                    shouldShowCovInfoButton: true
                },
                {
                    coverageId: 'PRHElectronicsCov',
                    shouldShowCovInfoButton: true
                }
            ];
        case 'Hundeforsikring':
        case 'Katteforsikring':
        case 'Rejseforsikring':
        case 'Rejseforsikring til enkeltperson':
        case 'Bilforsikring':
        case 'Børneforsikring':
        default:
            return [];
    }
};

const getCoveragesWithProps = (
    coverages: CoverageDTO[],
    flavourName: string,
    onsaCoverageTexts: CoverageTextItem[]
): AdditionalCoverageProps[] => {
    const coveragesProps = getCoveragesProps(flavourName);
    const additionalCoverages: AdditionalCoverageProps[] = [];

    coverages.forEach((coverage) => {
        const coverageProps = coveragesProps.find((cov) => cov.coverageId === coverage.coverageId);
        const coverageTexts = onsaCoverageTexts.find(
            (cov) => cov.coverageItemId === coverage.coverageId
        );
        if (coverageProps) {
            additionalCoverages.push({
                ...coverage,
                input: coverageProps.input,
                shouldShowCallMeModal: coverageProps.shouldShowCallMeModal,
                shouldShowConfirmationModal: coverageProps.shouldShowConfirmationModal,
                confirmationModalProps: {
                    modalHeader: coverageProps.confirmationModalProps?.modalHeader,
                    modalSubheader: coverageTexts?.nonCustomerDescription
                },
                inputModalProps: {
                    modalHeader: coverageProps.inputModalProps?.modalHeader,
                    modalSubheader: coverageTexts?.nonCustomerDescription
                },
                coverageDescription: coverageTexts?.subtitle,
                shouldShowCovInfoButton: coverageProps.shouldShowCovInfoButton,
                includedCoverageText: coverageTexts?.included?.items,
                excludedCoverageText: coverageTexts?.excluded?.items
            });
        } else {
            additionalCoverages.push({ ...coverage, coverageDescription: coverageTexts?.subtitle });
        }
    });
    return additionalCoverages;
};

const shouldShowCallMeModal = (
    coverable: CoverableOnsaDTO,
    coverage: AdditionalCoverageProps
): boolean => {
    switch (coverable.flavourName) {
        case 'Ulykkesforsikring': {
            if (coverage.coverageId === 'ACCHighRiskSportCov') {
                return (
                    (coverable as AdultAccidentOnsaDTO).selectedEmployment?.publicId === '1488' ||
                    coverable.coverages?.find(
                        (cov) => cov.coverageId === 'ACCImmediateCompensationCov'
                    ) !== undefined
                );
            }
            break;
        }
        case 'Ulykkesforsikring til unge':
            if (coverage.coverageId === 'ACCHighRiskSportCov') {
                return (
                    coverable.coverages?.find(
                        (cov) => cov.coverageId === 'ACCImmediateCompensationCov'
                    ) !== undefined
                );
            }
            break;
    }
    return coverage.shouldShowCallMeModal !== undefined;
};

const getCoverageFieldToUpdate = (coverageId: string): string => {
    switch (coverageId) {
        case 'PRCBikeComprehensiveCov':
            return 'bikeComprehensiveDeductible';
        default:
            return '';
    }
};

export { getCoveragesWithProps, shouldShowCallMeModal, getCoverageFieldToUpdate };
export type { AdditionalCoverageProps, ConfirmationModalProps };
